import { useCallback, useEffect, useRef } from "react";

import { useLatest } from "../useLatest";

const SMS_OTP_LISTEN_TIMEOUT_MS = 60 * 1000;

/**
 * Temporarily off. Requesting the credential makes Android show a consent dialog, but the code
 * never arrives because the SMS `@host` line does not match this origin — so users are asked for
 * permission and get nothing back. Set to true once the SMS template carries the right hostname.
 */
const SMS_OTP_AUTOFILL_ENABLED = false;

interface UseSmsOtpAutofillOptions {
    /** Called when the browser provides an SMS OTP via the Web OTP API. */
    onOtp: (code: string) => void;
    /** When false, listening is paused (e.g. while the OTP step is hidden). Defaults to true. */
    enabled?: boolean;
}

/**
 * Listens for SMS OTP via the Web OTP API (`OTPCredential`) where supported (mainly Chrome Android).
 * Pair with `autocomplete="one-time-code"` on OTP inputs for iOS Safari autofill.
 */
export function useSmsOtpAutofill({ onOtp, enabled = true }: UseSmsOtpAutofillOptions) {
    const onOtpRef = useLatest(onOtp);
    const controllerRef = useRef<AbortController | null>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const stopListening = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        controllerRef.current?.abort();
        controllerRef.current = null;
    }, []);

    const startListening = useCallback(() => {
        if (!SMS_OTP_AUTOFILL_ENABLED || typeof window === "undefined" || !("OTPCredential" in window)) {
            return;
        }

        stopListening();

        const controller = new AbortController();
        controllerRef.current = controller;
        timeoutRef.current = setTimeout(() => {
            controller.abort();
        }, SMS_OTP_LISTEN_TIMEOUT_MS);

        try {
            navigator.credentials
                .get({
                    signal: controller.signal,
                    otp: { transport: ["sms"] }
                })
                .then((credential) => {
                    if (credential?.type === "otp" && credential.code) {
                        onOtpRef.current(String(credential.code).trim());
                    }
                })
                .catch((error: { name?: string; message?: string }) => {
                    if (error?.name === "AbortError") {
                        return;
                    }

                    // Web OTP fails for reasons that are indistinguishable from the UI: another
                    // pending credential request, an insecure or cross-origin context, or the
                    // user dismissing the prompt. Swallowing it silently makes the whole flow
                    // look like it simply did nothing.
                    console.warn("[WebOTP] SMS OTP retrieval failed:", error?.name, error?.message);
                });
        } catch (error) {
            console.warn("[WebOTP] SMS OTP unavailable in this context:", error);
        }
    }, [onOtpRef, stopListening]);

    useEffect(() => {
        if (!enabled) {
            stopListening();
            return;
        }

        startListening();
        return stopListening;
    }, [enabled, startListening, stopListening]);

    return { startListening, stopListening };
}
